<template>
  <SafeTeleport to="#status-dropdown-target">
    <div>
      <Dropdown
        :items="statusOptions"
        :label="label"
        :disabled="isDisabled"
        @update:modelValue="handleStatusSelect"
      />
      <Modal
        v-if="modalOpen"
        title="Appointment Status Change"
        @hide-modal="hideModal"
        @submit="handleSave"
      >
        <div>
          <Alert type="warning" class="mb-3">
            <div v-if="lostFields.length">
              By changing the status to "<span class="font-bold">{{
                selectedStatus
              }}</span
              >" the following times will be reset.
            </div>
            <div v-else-if="requiredFields.length">
              By changing the status to "<span class="font-bold">{{
                selectedStatus
              }}</span
              >" you will need to update the times below to reflect the correct
              time.
            </div>
          </Alert>
          <AppointmentTimeline
            :appointment="appointment"
            :editable-times="requiredFields"
            :lost-times="lostFields"
            @update:modelValue="handleUpdateTime"
          />
          <TextArea
            v-model="reason"
            class="mt-4"
            message="Reason for status change"
          />
        </div>
      </Modal>
    </div>
  </SafeTeleport>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useFeatureFlag } from "@/lib/useFeatureFlag";

import Modal from "@/components/Modal.vue";
import Alert from "@/components/Seranova/Alert/Alert.vue";
import Dropdown from "@/components/Seranova/Dropdown/Dropdown.vue";
import TextArea from "@/components/Seranova/TextArea/TextArea.vue";
import CONSTANTS from "@/Constants";
import { AppointmentStatus, AppointmentV2 } from "@/typings/appointment";
const { APPOINTMENT_STATUS_ORDER, APPOINTMENT_STATUS_MAP } = CONSTANTS;
import { SafeTeleport } from "vue-safe-teleport";

import AppointmentTimeline from "./AppointmentTimeline.vue";
import { useAppointmentStatus } from "./useAppointmentStatus";

const emit = defineEmits(["refresh-appointment"]);
const props = defineProps<{ appointment: AppointmentV2 }>();
const isMultiTechEnabled = useFeatureFlag("multiple_technicians_ff_enabled");
const { displayStatus, getRequiredFieldsForStatus, times, changeStatus, getTimesToRemove } =
  useAppointmentStatus(props.appointment);
const selectedStatus = ref<AppointmentStatus | null>(null);
const modalOpen = ref(false);
const reason = ref("");
const links = computed(() => props.appointment.links || {});
const statusOptions = computed(() => {
  const availableStatuses = [props.appointment.status].concat(
    Object.keys(links.value)
  );
  // Sort statuses by flow
  const sortedStatuses = APPOINTMENT_STATUS_ORDER.filter((status) =>
    availableStatuses.includes(status)
  );
  return sortedStatuses
    .filter(
      (status) =>
        status !== props.appointment.status &&
        APPOINTMENT_STATUS_MAP[status] &&
        links.value[status]
    )
    .map((status) => ({
      value: status,
      label: APPOINTMENT_STATUS_MAP[status]
    }));
});
const isDisabled = computed(() => statusOptions.value.length === 0);
const requiredFields = computed(() => {
  if (!selectedStatus.value) return [];
  return getRequiredFieldsForStatus(selectedStatus.value);
});
const lostFields = computed(() => {
  if (!selectedStatus.value) return [];
  return getTimesToRemove(props.appointment.status, selectedStatus.value);
});

const label = computed(() => {
  const statusKey = isMultiTechEnabled.value
    ? props.appointment.display_status || props.appointment.status
    : props.appointment.status;
  return APPOINTMENT_STATUS_MAP[statusKey] || statusKey;
});
const handleStatusSelect = (item: { value: string; label: string }) => {
  selectedStatus.value = item.value as AppointmentStatus;
  modalOpen.value = true;
};
const hideModal = () => {
  modalOpen.value = false;
};

const handleSave = async () => {
  if (!selectedStatus.value) return;
  const statusLink = links.value[selectedStatus.value];
  if (!statusLink) {
    window.notify.error("Unable to edit the status of this appointment.");
    return;
  }
  await changeStatus(selectedStatus.value, times, reason.value, statusLink);
  modalOpen.value = false;
  window.notify.success("Appointment status updated");
  emit("refresh-appointment");
};
const handleUpdateTime = (data: { [k: string]: string }) => {
  const fieldName = Object.keys(data)[0];
  times[fieldName] = data[fieldName];
};
</script>

<style scoped></style>
